<template>
  <div class="flex h-[calc(100%-56px)]">
    <div class="flex-1 overflow-auto p-4 flex justify-between flex-col">
      <!-- List Audit Logs -->
      <div>
        <div>
          <p
            v-if="!uiFlags.fetchingList && !records.length"
            class="flex h-full items-center flex-col justify-center"
          >
            {{ $t('AUDIT_LOGS.LIST.404') }}
          </p>
          <woot-loading-state
            v-if="uiFlags.fetchingList"
            :message="$t('AUDIT_LOGS.LOADING')"
          />
          <div class="audit-log-container space-y-5">
            <div
              v-for="(group, date) in groupedRecords"
              :key="date"
              class="flex flex-col items-start"
            >
              <div class="date-split mb-3">
                <span
                  class="text-xs text-slate-900 bg-white rounded-md px-[10px] py-[5px]"
                >
                  {{ date }}
                </span>
              </div>
              <div class="space-y-3 w-full">
                <audit-log-item
                  v-for="auditLogItem in group"
                  :key="auditLogItem.id"
                  :item="auditLogItem"
                />
              </div>
            </div>
          </div>
          <audit-log-paginate
            v-if="totalPages > 1"
            :on-page-change="onPageChange"
          />
        </div>
      </div>
    </div>
    <woot-modal
      :show.sync="showFiltersModal"
      :on-close="closeAdvanceFiltersModal"
      size="medium"
      class="audit-advanced-filter-modal"
    >
      <audit-advanced-filters
        v-if="showFiltersModal"
        :on-close="closeAdvanceFiltersModal"
        :initial-filter-types="auditLogFilterItems"
        :initial-applied-filters="appliedFilter"
        @applyFilter="onApplyFilter"
        @clearFilters="clearFilters"
      />
    </woot-modal>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import AuditAdvancedFilters from './AuditAdvancedFilters.vue';
import AuditLogItem from './AuditLogItem.vue';
import AuditLogPaginate from './AuditLogPaginate.vue';
import { filterItems } from './auditlogsFilterItems';
import timeMixin from 'dashboard/mixins/time';
import alertMixin from 'shared/mixins/alertMixin';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

const DEFAULT_PAGE = 1;

export default {
  components: {
    AuditAdvancedFilters,
    AuditLogItem,
    AuditLogPaginate,
  },
  mixins: [alertMixin, timeMixin],
  data() {
    return {
      loading: {},
      auditLogsAPI: {
        message: '',
      },
      showFiltersModal: false,
      appliedFilter: [],
      lastAppliedFilter: [],
      segmentsId: null,
      auditLogFilterItems: filterItems.map(filter => ({
        ...filter,
        attributeName: this.$t(
          `AUDIT_LOGS_FILTER.ATTRIBUTES.${filter.attributeI18nKey}`
        ),
      })),
    };
  },
  computed: {
    ...mapGetters({
      records: 'auditlogs/getAuditLogs',
      uiFlags: 'auditlogs/getUIFlags',
      meta: 'auditlogs/getMeta',
      agentList: 'agents/getAgents',
      getAppliedAuditlogsFilters: 'auditlogs/getAppliedAuditlogsFilters',
      getSearchQuery: 'auditlogs/getSearchQuery',
      getExportType: 'auditlogs/getCurrentExportType',
    }),
    hasAppliedFilters() {
      return this.getAppliedAuditlogsFilters.length;
    },
    totalPages() {
      return Math.ceil(this.meta.totalEntries / this.meta.perPage) || 1;
    },
    groupedRecords() {
      return this.records.reduce((groups, item) => {
        const date = this.messageTimestamp(
          item.created_at,
          this.$root.$i18n.locale === 'en' ? 'MMM dd, yyyy' : 'dd MMM yyyy'
        );
        if (!groups[date]) groups[date] = [];
        groups[date].push(item);
        return groups;
      }, {});
    },
  },
  mounted() {
    // Fetch API Call
    this.$store.dispatch('agents/get');

    // Fetch audit logs based on current export type (preserve user's last action)
    const page = this.$route.query.page ?? DEFAULT_PAGE;
    switch (this.getExportType) {
      case 'search':
        this.submitSearch(page);
        break;
      case 'filter':
        this.fetchFilteredAuditLogs(page);
        break;
      default:
        this.fetchAuditLogs(page);
        break;
    }

    // Listen for filter modal open event from AuditFilter component
    this.$root.$on('openAuditFiltersModal', this.openAdvanceFiltersModal);
    this.$root.$on('onSubmitSearch', this.submitSearch);
  },
  beforeDestroy() {
    // Clean up event listener
    this.$root.$off('openAuditFiltersModal', this.openAdvanceFiltersModal);
    this.$root.$off('onSubmitSearch', this.submitSearch);
  },
  methods: {
    fetchAuditLogs(page = 1) {
      this.$store.dispatch('auditlogs/fetch', { page }).catch(error => {
        const errorMessage =
          error?.message || this.$t('AUDIT_LOGS.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      });
    },
    onPageChange(page) {
      this.updatePageParam(page);
      try {
        // Determine which API to call based on export type
        switch (this.getExportType) {
          case 'search':
            this.submitSearch(page);
            break;
          case 'filter':
            this.fetchFilteredAuditLogs(page);
            break;
          default:
            this.fetchAuditLogs(page);
            break;
        }
      } catch (error) {
        const errorMessage =
          error?.message || this.$t('AUDIT_LOGS.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
    submitSearch(page) {
      // Clear any applied filters when searching
      if (this.hasAppliedFilters) {
        this.appliedFilter = [];
        this.$store.dispatch('auditlogs/clearAuditLogsFilters');
        this.$store.commit('auditlogs/SET_AUDIT_LOGS_FILTER_QUERY', null);
      }

      if (this.getSearchQuery) {
        this.$store.commit('auditlogs/SET_AUDIT_LOGS_EXPORT_TYPE', 'search');
        this.$store.dispatch('auditlogs/search', {
          search: this.getSearchQuery,
          page: page || 1,
        });
      } else {
        this.fetchAuditLogs(page);
      }
    },
    openAdvanceFiltersModal() {
      this.showFiltersModal = true;
    },
    closeAdvanceFiltersModal() {
      this.appliedFilter = this.lastAppliedFilter;
      this.$store.commit(
        'auditlogs/SET_AUDIT_LOGS_FILTER_QUERY',
        this.appliedFilter
      );
      this.showFiltersModal = false;
    },
    onApplyFilter(payload) {
      this.lastAppliedFilter = payload;
      this.segmentsQuery = filterQueryGenerator(payload);
      this.$store.commit(
        'auditlogs/SET_AUDIT_LOGS_FILTER_QUERY',
        this.segmentsQuery
      );
      this.$store.commit('auditlogs/SET_AUDIT_LOGS_EXPORT_TYPE', 'filter');
      this.fetchFilteredAuditLogs(DEFAULT_PAGE);
      this.showFiltersModal = false;
    },
    clearFilters() {
      this.appliedFilter = [];
      this.lastAppliedFilter = [];
      this.$store.dispatch('auditlogs/clearAuditLogsFilters');
      this.$store.commit('auditlogs/SET_AUDIT_LOGS_FILTER_QUERY', null);
      this.$store.commit('auditlogs/SET_AUDIT_LOGS_EXPORT_TYPE', null);
      this.showFiltersModal = false;
      this.fetchAuditLogs();
    },
    updatePageParam(page) {
      window.history.pushState({}, null, `${this.$route.path}?page=${page}`);
    },
    // eslint-disable-next-line no-unused-vars
    fetchSavedFilteredAuditLogs(payload, page) {},
    fetchFilteredAuditLogs(page) {
      if (this.hasAppliedFilters) {
        this.$store.dispatch('auditlogs/filter', {
          queryPayload: this.segmentsQuery,
          page,
        });
      } else {
        this.$store.dispatch('auditlogs/fetch', { page });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.date-split {
  display: flex;
  align-items: center;
  align-self: stretch;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
    margin-left: 10px;
  }
}

[dir='rtl'] {
  .date-split::after {
    margin-right: 10px;
  }
}
</style>
